import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Database, Loader2, RotateCcw, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminDemoStatus, adminDemoRestore, adminDemoDelete } from "@/lib/api";
import { DemoLinksPanel } from "@/components/DemoLinksPanel";

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

export default function AdminDemo() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const d = await adminDemoStatus();
      setItems(d.companies || []);
      return d.job || {};
    } catch (e) {
      toast.error(errMsg(e, "No se pudo leer el estado de la demo."));
      return {};
    } finally {
      setLoading(false);
    }
  };

  const waitForJob = async () => {
    for (;;) {
      const job = await load(true);
      if (!job.running) return job;
      await new Promise((r) => setTimeout(r, 3000));
    }
  };

  useEffect(() => {
    load().then((job) => {
      if (job.running) { setBusy("restore"); waitForJob().finally(() => setBusy(null)); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "restore") {
        await adminDemoRestore();
        const job = await waitForJob();
        if (job.error) throw new Error(job.error);
        toast.success("Datos demo restaurados: SimpleTV y FinFarma.");
      } else {
        await adminDemoDelete();
        toast.success("Datos demo eliminados.");
      }
      await load();
    } catch (e) {
      toast.error(errMsg(e, "La operación falló."));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-white text-ink" data-testid="admin-demo-page">
      <TopNav />
      <div className="mx-auto max-w-5xl px-6 py-12 sm:py-16">
        <p className="eyebrow flex items-center gap-1.5">
          <Database className="h-3 w-3" /> Administración · Datos demo
        </p>
        <h1 className="mt-2 font-display text-4xl text-ink sm:text-5xl">Empresas demo</h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600">
          Carga o elimina las empresas de demostración <strong>SimpleTV</strong> y <strong>FinFarma</strong>:
          entrevista ABC completa, informe ejecutivo, agentes por departamento, biblioteca KBL,
          6 meses de interacciones y eventos, planes de facturación y tenants. Restaurar sobrescribe
          la demo existente sin tocar el resto de los datos.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <ConfirmButton
            testId="demo-restore-btn"
            icon={RotateCcw}
            label="Restaurar datos demo"
            title="¿Restaurar los datos demo?"
            description="Se recrearán SimpleTV y FinFarma desde cero (informes, agentes, KBL, interacciones y planes). Los cambios hechos sobre la demo se perderán."
            busy={busy === "restore"}
            disabled={!!busy}
            onConfirm={() => run("restore")}
          />
          <ConfirmButton
            testId="demo-delete-btn"
            icon={Trash2}
            label="Eliminar datos demo"
            variant="outline"
            title="¿Eliminar los datos demo?"
            description="Se borrarán todas las sesiones, agentes, documentos, interacciones, planes y tenants de SimpleTV y FinFarma. Usuarios y configuración no se tocan."
            busy={busy === "delete"}
            disabled={!!busy}
            onConfirm={() => run("delete")}
          />
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2" data-testid="demo-status-list">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-400">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
            </div>
          ) : (
            items.map((c) => <DemoCard key={c.key} c={c} />)
          )}
        </div>

        <DemoLinksPanel />
      </div>
    </div>
  );
}

function ConfirmButton({ testId, icon: Icon, label, title, description, busy, disabled, onConfirm, variant }) {
  const solid = variant !== "outline";
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          data-testid={testId}
          disabled={disabled}
          variant={solid ? "default" : "outline"}
          className={`rounded-sm text-[11px] uppercase tracking-[0.2em] ${
            solid ? "bg-slate-900 text-white hover:bg-slate-800" : "border-red-300 text-red-700 hover:bg-red-50"
          }`}
        >
          {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <Icon className="mr-2 h-3.5 w-3.5" />}
          {busy ? "Procesando…" : label}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`${testId}-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>Cancelar</AlertDialogCancel>
          <AlertDialogAction data-testid={`${testId}-confirm`} onClick={onConfirm}>
            Confirmar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DemoCard({ c }) {
  return (
    <article className="border border-slate-200 bg-white p-5" data-testid={`demo-card-${c.key}`}>
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl text-ink">{c.company}</h3>
        <span
          className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] ${
            c.loaded ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"
          }`}
          data-testid={`demo-status-${c.key}`}
        >
          {c.loaded ? "Cargada" : "No cargada"}
        </span>
      </div>
      <dl className="mt-4 grid grid-cols-4 gap-2 text-center">
        {[["Agentes", c.agents], ["Chats", c.chats], ["Eventos", c.events], ["KBL", c.kbl_documents]].map(([k, v]) => (
          <div key={k} className="border border-slate-100 bg-slate-50/60 px-2 py-2">
            <dd className="font-display text-base font-semibold text-slate-900">{v}</dd>
            <dt className="text-[9px] uppercase tracking-[0.18em] text-slate-500">{k}</dt>
          </div>
        ))}
      </dl>
      {c.loaded && (
        <Link
          to={`/informe/${c.session_id}`}
          className="mt-4 inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-accent-blue hover:underline"
          data-testid={`demo-report-link-${c.key}`}
        >
          Ver informe ejecutivo <ExternalLink className="h-3 w-3" />
        </Link>
      )}
    </article>
  );
}
